/**
 * Request/Response Panel Component
 *
 * Displays API request and response details for transparency.
 * JWT tokens are redacted.
 */

import { useState } from 'react';
import type { ApiResult } from '../lib/types';

interface RequestPanelProps {
  result: ApiResult | null;
  loading?: boolean;
}

export function RequestPanel({ result, loading }: RequestPanelProps) {
  const [activeTab, setActiveTab] = useState<'request' | 'response'>('response');

  if (!result && !loading) {
    return null;
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'request' ? 'active' : ''}`}
          onClick={() => setActiveTab('request')}
        >
          Request
        </button>
        <button
          className={`tab ${activeTab === 'response' ? 'active' : ''}`}
          onClick={() => setActiveTab('response')}
        >
          Response
          {result && (
            <span
              style={{
                marginLeft: 8,
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: 11,
                background:
                  result.response.status >= 200 && result.response.status < 300
                    ? '#dcfce7'
                    : result.response.status >= 400
                      ? '#fee2e2'
                      : '#e5e5e5',
                color:
                  result.response.status >= 200 && result.response.status < 300
                    ? '#166534'
                    : result.response.status >= 400
                      ? '#991b1b'
                      : '#525252',
              }}
            >
              {result.response.status || 'ERR'}
            </span>
          )}
        </button>
      </div>

      {loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>Loading...</div>
      )}

      {!loading && result && activeTab === 'request' && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                background: '#e5e5e5',
                borderRadius: 4,
                fontWeight: 600,
                marginRight: 8,
              }}
            >
              {result.request.method}
            </span>
            <code className="mono" style={{ wordBreak: 'break-all' }}>
              {result.request.url}
            </code>
          </div>

          <h4 style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Headers</h4>
          <pre style={{ marginBottom: 16 }}>
            {Object.entries(result.request.headers).map(([key, value]) => (
              <div key={key}>
                <span style={{ color: '#9cdcfe' }}>{key}</span>:{' '}
                <span style={{ color: '#ce9178' }}>{value}</span>
              </div>
            ))}
          </pre>

          {result.request.body !== undefined && (
            <>
              <h4 style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Body</h4>
              <pre>{JSON.stringify(result.request.body, null, 2)}</pre>
            </>
          )}
        </div>
      )}

      {!loading && result && activeTab === 'response' && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                background:
                  result.response.status >= 200 && result.response.status < 300
                    ? '#dcfce7'
                    : result.response.status >= 400
                      ? '#fee2e2'
                      : '#e5e5e5',
                color:
                  result.response.status >= 200 && result.response.status < 300
                    ? '#166534'
                    : result.response.status >= 400
                      ? '#991b1b'
                      : '#525252',
                borderRadius: 4,
                fontWeight: 600,
                marginRight: 8,
              }}
            >
              {result.response.status || 'ERR'}
            </span>
            <span style={{ color: '#666' }}>{result.response.statusText}</span>
          </div>

          {result.response.error && (
            <div className="error-box" style={{ marginBottom: 16 }}>
              <strong>{result.response.error.error}</strong>
              <div>{result.response.error.message}</div>
              {result.response.error.details && (
                <pre style={{ marginTop: 8, background: 'transparent', padding: 0, color: 'inherit' }}>
                  {JSON.stringify(result.response.error.details, null, 2)}
                </pre>
              )}
            </div>
          )}

          {result.response.data !== undefined && (
            <>
              <h4 style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Response Body</h4>
              <pre>
                {typeof result.response.data === 'string'
                  ? result.response.data.slice(0, 5000) +
                    (result.response.data.length > 5000 ? '\n... (truncated)' : '')
                  : JSON.stringify(result.response.data, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact status indicator for inline use.
 */
export function StatusBadge({ status }: { status: string }) {
  const statusClass = `status status-${status.toLowerCase().replace(/_/g, '_')}`;
  return <span className={statusClass}>{status}</span>;
}
